import React from 'react';
import { Monitor } from 'lucide-react';
import { slideText } from '../lib/slides';
import { PREVIEW_LABEL_RATIO, PREVIEW_BODY_RATIO, PREVIEW_SONG_BODY_RATIO, PREVIEW_WELCOME_RATIO, PREVIEW_WELCOME_SUBTITLE_RATIO, PREVIEW_LABEL_MARGIN } from './previewRatios';

/** The blue Live card: what is on the projector, with lyric stepping and Clear. */
const LiveCard = ({
  dm, isLive, displayContent, currentLyricLine, slides, settings, previewBg, previewText,
  prevLyricLine, resetLyricLine, nextLyricLine, clearLiveContent,
}) => {
  const total = Array.isArray(slides) ? slides.length : 0;
  const isSong = displayContent.type === 'song';
  // Songs always get the stepper; a passage only when it was split into several slides.
  const stepper = isSong || (displayContent.type === 'scripture' && total > 1);
  const noun = isSong ? 'line' : 'slide';
  const atEnd = total > 0 ? currentLyricLine >= total - 1 : true;
  return (
    <div className={`rounded-2xl p-4 border-2 shadow-lg ring-2 ring-brand-600/30 ${dm ? 'bg-gradient-to-br from-blue-900/30 to-slate-900/60 border-blue-500/50' : 'bg-white border-blue-300'}`}>
      <div className="flex items-center justify-between mb-2">
        <h2 className={`text-base font-bold flex items-center gap-2 ${dm ? 'text-blue-300' : 'text-blue-700'}`}>
          <div className="p-1.5 bg-brand-600 rounded-lg">
            <Monitor className="w-3.5 h-3.5 text-slate-50" />
          </div>
          Live
          {isLive && (
            <span className="ml-1 text-xs text-red-400 animate-pulse font-bold flex items-center gap-1">
              <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse shadow-lg"></span>
              ON
            </span>
          )}
        </h2>
        <div className="flex gap-1 items-center">
          {stepper && (
            <>
              <button
                onClick={prevLyricLine}
                disabled={currentLyricLine === 0}
                title={`Previous ${noun}`}
                className="px-2 py-1 bg-gradient-to-r from-blue-600 to-blue-700 text-slate-50 rounded text-xs font-semibold hover:from-blue-500 hover:to-blue-600 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              >
                ←
              </button>
              <button
                onClick={resetLyricLine}
                title={isSong ? 'Restart song' : 'Back to the first slide'}
                className="px-3 py-1.5 bg-gradient-to-r from-blue-700 to-blue-600 text-slate-50 rounded text-xs font-semibold hover:from-blue-600 hover:to-blue-500 transition-all"
              >
                ⟲
              </button>
              {total > 1 && (
                <span className={`text-xs font-semibold tabular-nums ${dm ? 'text-blue-200' : 'text-blue-700'}`} aria-label="Slide position">
                  {Math.min(currentLyricLine, total - 1) + 1}/{total}
                </span>
              )}
              <button
                onClick={nextLyricLine}
                disabled={atEnd}
                title={`Next ${noun}`}
                className="px-2 py-1 bg-gradient-to-r from-blue-600 to-blue-700 text-slate-50 rounded text-xs font-semibold hover:from-blue-500 hover:to-blue-600 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              >
                →
              </button>
            </>
          )}
          {displayContent.type !== 'welcome' && (
            <button
              onClick={clearLiveContent}
              title="Clear live display"
              className="px-3 py-1.5 border border-red-500/40 bg-red-500/10 text-red-500 rounded text-xs font-semibold hover:bg-red-600 hover:text-white transition-all"
            >
              Clear
            </button>
          )}
        </div>
      </div>
      <div
        className="rounded-lg overflow-hidden shadow-lg flex items-center justify-center p-4 border border-slate-700/20"
        style={{
          ...previewBg.style,
          color: settings.fontColor,
          minHeight: '120px',
          position: 'relative'
        }}
      >
        {previewBg.dim > 0 && (
          <div className="absolute inset-0" style={{ background: `rgba(0,0,0,${previewBg.dim})` }} />
        )}
        <div className="text-center max-w-full relative" style={previewText}>
          {displayContent.type === 'scripture' ? (
            <div>
              <div style={{ fontSize: `${settings.fontSize * PREVIEW_LABEL_RATIO}px`, fontFamily: settings.fontFamily, fontWeight: 'bold', marginBottom: PREVIEW_LABEL_MARGIN }}>
                {displayContent.reference} {displayContent.version && `(${displayContent.version})`}
              </div>
              <div style={{ fontSize: `${settings.fontSize * PREVIEW_BODY_RATIO}px`, fontFamily: settings.fontFamily, lineHeight: '1.4' }}>
                {slideText(displayContent, slides, currentLyricLine)}
              </div>
            </div>
          ) : displayContent.type === 'song' ? (
            <div>
              <div style={{ fontSize: `${settings.fontSize * PREVIEW_SONG_BODY_RATIO}px`, fontFamily: settings.fontFamily, lineHeight: '1.6' }}>
                {slideText(displayContent, slides, currentLyricLine)}
              </div>
            </div>
          ) : (
            // #3B82F6 = brand-500 (tailwind.config.js); inline because this
            // preview mirrors settings.fontFamily/fontSize dynamically
            <div style={{ textAlign: 'center', color: '#3B82F6' }}>
              <div style={{ fontSize: `${settings.fontSize * PREVIEW_WELCOME_RATIO}px`, fontFamily: settings.fontFamily, fontWeight: 'bold', marginBottom: PREVIEW_LABEL_MARGIN }}>
                SleekHear
              </div>
              <div style={{ fontSize: `${settings.fontSize * PREVIEW_WELCOME_SUBTITLE_RATIO}px`, fontFamily: settings.fontFamily, letterSpacing: '4px' }}>
                CHURCH PRESENTATION SYSTEM
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LiveCard;
